import moment from 'moment';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { playerTypeEnum } from 'common/commonEnum';
import { ICasinoReport } from 'appRedux/models/casinoModels';
import { InsightValue } from './types';
import { config } from 'config/config';
import {
  ATTEMPS_LIMIT_EXCEEDED_MESSAGE,
  INVALID_OTP_CODE_MESSAGE,
  INVALID_OTP_CODE_MESSAGE2,
  MUST_HAVE_LOWERCASE_CHAR_MESSAGE,
  MUST_HAVE_SYMBOL_CHAR_MESSAGE,
  MUST_HAVE_UPPERCASE_CHAR_MESSAGE,
  parseValue,
  SHORT_PASSWORD_MESSAGE,
  TOP_SLOTS_LENGTH,
} from './include';
import { AwsRumConfig } from 'aws-rum-web';
import {
  floormapHeatBeanCount as floormapHeatBeanCountValue,
  floormapJackpotColdHeatScale as floormapJackpotColdHeatScaleValue,
  floormapMergedNeutralColor,
  floormapMergedNeutralLightColor,
  floormapSourceNtileCount as floormapSourceNtileCountValue,
  floormapTopHeatScale as floormapTopHeatScaleValue,
  heatLegendAllInsight,
  heatLegendJackpots,
  isJackpotInsight,
  mapNtileToFloormapHeat,
} from './floormapHeat';
import { displayIdForInsightMnum } from 'lib/machineCatalog';
import { getMachineDisplayId } from 'lib/machineDisplayId';
library.add(fas);

export { toPercentage } from './toPercentage';

/** 1 being the coldest slot and 6 being the hottests slot */
export enum slotHotnessScale {
  Six = 6,
  Five = 5,
  Four = 4,
  Three = 3,
  Two = 2,
  One = 1,
}
export {
  floormapMergedNeutralColor,
  floormapMergedNeutralLightColor,
  heatLegendAllInsight,
  heatLegendJackpots,
  isJackpotInsight,
  mapNtileToFloormapHeat,
};

export const floormapSourceNtileCount = floormapSourceNtileCountValue as slotHotnessScale;
export const floormapHeatBeanCount = floormapHeatBeanCountValue as slotHotnessScale;
export const floormapTopHeatScale = floormapTopHeatScaleValue as slotHotnessScale;
export const floormapJackpotColdHeatScale = floormapJackpotColdHeatScaleValue as slotHotnessScale;

export enum OrderByEnum {
  Asc = 'asc',
  Desc = 'desc',
}
export type OrderValueType =
  | 'jackpot_value_order'
  | 'money_won_order'
  | 'spins_order'
  | 'avg_money_won_per_spin_order'
  | 'alias_desc_order'
  | 'alias_asc_order';
export type MapType = keyof typeof MapTypeEnum;
export enum MapTypeEnum {
  slotReportMap = 'slotReportMap',
  individualReportMap = 'individualReportMap',
}

export enum TimeFrameValueEnum {
  hours1 = 'hours1',
  hours4 = 'hours4',
  hours8 = 'hours8',
  hours24 = 'hours24',
  weeks1 = 'weeks1',
  months1 = 'months1',
  today1 = 'today1',
  yesterday1 = 'yesterday1',
  sinceLastJackpot = 'sinceLastJackpot',
  sinceLastVisit = 'sinceLastVisit',
  lastThreeYears = 'lastThreeYears',
  allJackpots = 'allJackpots',
  all = 'all',
}
export type TimeFrameValueType = keyof typeof TimeFrameValueEnum;

export enum OrderValueEnum {
  JackpotOrder = 'jackpot_value_order',
  MoneyWonOrder = 'money_won_order',
  SpinsOrder = 'spins_order',
  AvgMoneyWonPerSpinOrder = 'avg_money_won_per_spin_order',
  AliasDescOrder = 'alias_desc_order',
  AliasAscOrder = 'alias_asc_order',
}
export type ListReportType = 'standard' | 'myplay' | 'jackpot' | 'favorites';
export enum ListReportEnum {
  Standard = 'standard',
  MyPlay = 'myplay',
  MyJackpot = 'jackpot',
  Favorites = 'favorites',
}

export enum FilterTypeEnum {
  leaderboard = 'leaderboard',
  jackpot = 'jackpot',
  myplay = 'myplay',
  graphfilter = 'graphfilter',
  mapfilter = 'mapfilter',
  snapfilter = 'snapfilter',
  main = 'main',
  hottestSlots = 'hottestSlots',
}
export type IFilterType = keyof typeof FilterTypeEnum;

export type ToolTipPositionType = keyof typeof ToolTipPositionEnum;
export enum ToolTipPositionEnum {
  top = 'top',
  bottom = 'bottom',
  left = 'left',
  right = 'right',
}

export type ToolTipAlignType = keyof typeof ToolTipAlignEnum;
export enum ToolTipAlignEnum {
  start = 'start',
  center = 'center',
  end = 'end',
}

export interface IInsightAccess {
  value: InsightValue;
  playerTypeAccess: playerTypeEnum[];
}

export interface IGraphData {
  group?: string;
  icon: string;
  name: string;
  axes: string;
  value: InsightValue;
  vaultFormat?: string;
  valueFormatSymbol?: string;
  displayAxesText: boolean;
  isPrimary: boolean;
  label?: string;
  playerTypeAccess?: playerTypeEnum[];
}

export type InsightType = {
  id: number;
  value: InsightValue;
  field: string;
  label: string;
  short_name: string;
  trendLabel: string;
  hotColdLabel: string;
  insightFieldsSelectorLabel: string;
  graphLabel: string;
  color: string;
  backgroundColor: string;
  enableDarkBg: boolean;
  className: string;
  description: string; // To be added
  display_order: number;
  insightId: number;
  icon: string; // To be added
  playerTypeAccess: playerTypeEnum[];
  highVoltilityLabel?: string; // Optional property
  lowVoltilityLabel?: string; // Optional property
  includeInTrends: boolean; // indicates if the insight should be included in trends
  orderInTrends?: number; // Optional property - indicates if order the insihgt to show in trends
  graphDescription?: string; // Optional property
  graphPlayerTypeAccess?: playerTypeEnum[];
  graphButtonName: string;
  enableMainPullDown: boolean;
  enableInsight?: boolean; // Optional property - indicates if the insight should be enabled for main insight pulldown
  enableGraph?: boolean; // Optional property - indicates if the insight should be enabled for graph display
  enableGraphPulldown: boolean; //indicates if the insight should be enabled for graph pulldown
  graphPulldownOrder?: number; // Optional property - indicates the order of the insight in the graph pulldown
  enableGraphButton?: boolean; // Optional property
  enableGraphButtonOrder?: number; // Optional property
  graphData: IGraphData[]; // At least 1 required
  compareLabel: string; // Optional property - label for compare view
  compareEnabled: boolean; // indicates if the insight should be enabled for compare view
  compareOrder?: number; // Optional property - indicates the order of the insight in compare view
  defaultCompareSelected: boolean; // indicates if the insight should be selected by default in compare view
};

export type SnapshotMetric = {
  id: number;
  field: string;
  value: string;
  label: string;
  snapshot_label: string;
  short_name: string;
  description: string;
};

export type SnapshotMetricGroup = {
  display_order: number;
  className: string;
  items: SnapshotMetric[];
};

export type SlotMetricsData = {
  snapShotArr: SnapshotMetricGroup[];
};

export type pinArrayType = {
  label: string;
  id: number;
  index: number;
};

export const getSubscriptionNameAndLogo = (isProUser: boolean) => {
  if (isProUser) {
    return {
      name: 'Slot Pro',
      logo: 'assets/images/SlotPro.png',
    };
  } else {
    return {
      name: 'Slot Check',
      logo: 'assets/images/Check.png',
    };
  }
};

export interface ISlotData {
  name: string;
  gameTheme?: string;
  denom: string;
  /**
   * ID for favorites / LikeButton / API selection.
   * Legacy: floor slot number. Insight: mnum (EGM id), never floorLocation.
   */
  slotNumber: number | string;
  /**
   * DISPLAY-ONLY identifier (machines.slotNumber, else mnum).
   * Prefer this on UI labels; keep slotNumber (below) for selection/favorites.
   */
  displayId?: string;
  pop: string;
  popNtile: number;
  rtp: string;
  rtpNtile: number;
  jackpots: string;
  jackpotsNtile: number;
  spins: string;
  spinsNtile: number;
  coinIn: string;
  coinInNtile: number;
  coinOut: string;
  coinOutNtile: number;
  volatility: string;
  volatilityNtile: number;
  avgWinSpin: string;
  avgWinSpinNtile: number;
  nonJackpotWinSpin: string;
  nonJackpotWinSpinNtile: number;
  jackpotValuePerSpin: string;
  jackpotValuePerSpinNtile: number;
  // winRate: string;
  // winRateNtile: number;
}

/**
 * RTP value for parseValue(…, 'return'). Insight rows (casinoInsight) carry
 * `return` as a PERCENT (44.51); divide by 100 once. Rows already converted
 * (returnIsFraction, set by withReturnFraction) and non-insight rows pass
 * through unchanged. null/undefined -> null so 0 stays a real value (0%).
 */
export const slotRtpValue = (row: ICasinoReport): number | null => {
  const r = row as unknown as { return?: unknown; isInsightRow?: unknown; returnIsFraction?: unknown };
  if (r.return === null || r.return === undefined || r.return === '') return null;
  const n = Number(r.return);
  if (!Number.isFinite(n)) return null;
  return r.isInsightRow === true && r.returnIsFraction !== true ? n / 100 : n;
};

export const convertToSlotData = (resSlotData: ICasinoReport): ISlotData => {
  const isInsight = (resSlotData as unknown as { isInsightRow?: unknown }).isInsightRow === true;
  const rtpValue = slotRtpValue(resSlotData);
  const insightMnum = (resSlotData as unknown as { mnum?: unknown }).mnum;
  const slotNumber = isInsight
    ? (insightMnum !== null && insightMnum !== undefined && String(insightMnum).trim() !== ''
        ? (Number.isFinite(Number(insightMnum)) ? Number(insightMnum) : String(insightMnum))
        : resSlotData.slot_number)
    : resSlotData.slot_number;
  return {
    name: resSlotData.game_name || 'Unknown Game',
    gameTheme: resSlotData.game_theme || '',
    // Insight: EGM id is mnum (selection/favorites). Legacy: slot_number.
    slotNumber,
    // DISPLAY-ONLY: matched GetSearchData machines.slotNumber, else mnum (never floorLocation).
    displayId: (isInsight
      ? displayIdForInsightMnum(insightMnum)
      : getMachineDisplayId(resSlotData as unknown as { slotNumber?: unknown; mnum?: unknown })) ||
      String(slotNumber ?? ''),
    denom: resSlotData.denom || 'N/A',
    pop: `${parseValue(resSlotData.pop, 'pop')}`,
    popNtile: resSlotData.pop_ntile || 1,
    // Insight: percent -> fraction once, 0 -> 0%. Non-insight: unchanged truthiness check.
    rtp: isInsight
      ? rtpValue === null
        ? 'N/A'
        : `${parseValue(rtpValue, 'return')}`
      : resSlotData.return
      ? `${parseValue(resSlotData.return, 'return')}`
      : 'N/A',
    rtpNtile: resSlotData.return_ntile || 1,
    jackpots: resSlotData.jackpot_value
      ? `${parseValue(resSlotData.jackpot_value, 'jackpot_value')}`
      : 'N/A',
    jackpotsNtile: resSlotData.jackpots_ntile || 1,
    spins: resSlotData.spins ? `${parseValue(resSlotData.spins, 'spins')}` : 'N/A',
    spinsNtile: resSlotData.spins_ntile || 1,
    coinIn: resSlotData.money_played
      ? `${parseValue(resSlotData.money_played, 'money_played')}`
      : 'N/A',
    coinInNtile: resSlotData.money_played_ntile || 1,

    // Additional insights
    coinOut: resSlotData.money_won ? `${parseValue(resSlotData.money_won, 'money_won')}` : 'N/A',
    coinOutNtile: resSlotData.money_won_ntile || 1,
    volatility: resSlotData.volatility_rating
      ? `${parseValue(resSlotData.volatility_rating_ntile, 'volatility_rating')}`
      : 'N/A',
    volatilityNtile: resSlotData.volatility_rating_ntile || 1,
    avgWinSpin: resSlotData.avg_money_won_per_spin
      ? `${parseValue(resSlotData.avg_money_won_per_spin, 'avg_money_won_per_spin')}`
      : 'N/A',
    avgWinSpinNtile: resSlotData.avg_money_won_per_spin_ntile || 1,
    nonJackpotWinSpin: resSlotData.non_jkpt_won_per_spin
      ? `${parseValue(resSlotData.non_jkpt_won_per_spin, 'non_jkpt_won_per_spin')}`
      : 'N/A',
    nonJackpotWinSpinNtile: resSlotData.non_jkpt_won_per_spin_ntile || 1,
    jackpotValuePerSpin: resSlotData.jackpot_value_per_spin
      ? `${parseValue(resSlotData.jackpot_value_per_spin, 'jackpot_value_per_spin')}`
      : 'N/A',
    jackpotValuePerSpinNtile: resSlotData.jackpot_value_per_spin_ntile || 1,
    // winRate: resSlotData.win_rate ? `${parseValue(resSlotData.win_rate, 'win_rate')}` : 'N/A',
    // winRateNtile: resSlotData.win_rate_ntile || 1,
  };
};

export const getNearestTimeFrame = (timeFrame: string) => {
  let fromDate: any;
  const toDate = moment();
  switch (timeFrame) {
    case TimeFrameValueEnum.hours1:
      fromDate = moment(toDate).subtract(1, 'hour');
      break;
    case TimeFrameValueEnum.hours4:
      fromDate = moment(toDate).subtract(4, 'hour');
      break;
    case TimeFrameValueEnum.hours8:
      fromDate = moment(toDate).subtract(8, 'hour');
      break;
    case TimeFrameValueEnum.hours24:
      fromDate = moment(toDate).subtract(24, 'hour');
      break;
    case TimeFrameValueEnum.yesterday1:
      fromDate = moment(toDate).subtract(1, 'day');
      break;
    case TimeFrameValueEnum.today1:
      fromDate = moment(toDate).subtract(24, 'hour');
      break;
    case TimeFrameValueEnum.weeks1:
      fromDate = moment(toDate).subtract(1, 'week').subtract(1, 'day');
      break;
    case TimeFrameValueEnum.months1:
      fromDate = moment(toDate).subtract(1, 'month').subtract(1, 'day');
      break;
    default:
      fromDate = moment(toDate).subtract(1, 'month').subtract(1, 'day');
      break;
  }
  if (timeFrame === TimeFrameValueEnum.today1 || timeFrame === TimeFrameValueEnum.yesterday1) {
    return fromDate.startOf('day').format();
  }
  if (timeFrame === TimeFrameValueEnum.sinceLastJackpot || timeFrame === TimeFrameValueEnum.all) {
    return;
  }
  return fromDate;
};

export const getLastTwoDigits = (slotNumber: number) => {
  return (slotNumber % 100).toString().padStart(2, '0');
};

export const getLastUpdatedDate = (datatime: any) => moment(datatime).format('MMM D, h:mma');

export const displayTimeFrame = (timeFrame: string) => {
  let fromDate: any;
  let toDate = moment();
  // Round down to nearest 5 minute bucket
  const numMinutes = Math.floor(toDate.minutes() / 5) * 5;
  toDate = toDate.minutes(numMinutes);
  switch (timeFrame) {
    case TimeFrameValueEnum.hours1:
      fromDate = moment(toDate).subtract(1, 'hour');
      break;
    case TimeFrameValueEnum.hours4:
      fromDate = moment(toDate).subtract(4, 'hour');
      break;
    case TimeFrameValueEnum.hours8:
      fromDate = moment(toDate).subtract(8, 'hour');
      break;
    case TimeFrameValueEnum.hours24:
      fromDate = moment(toDate).subtract(24, 'hour');
      break;
    case TimeFrameValueEnum.yesterday1:
      fromDate = moment(toDate).subtract(1, 'day').startOf('day');
      toDate = moment(toDate).subtract(1, 'day').endOf('day');
      break;
    case TimeFrameValueEnum.today1:
      fromDate = moment(toDate).startOf('day');
      break;
    case TimeFrameValueEnum.weeks1:
      fromDate = moment(toDate).subtract(1, 'week');
      break;
    case TimeFrameValueEnum.months1:
      fromDate = moment(toDate).subtract(1, 'month');
      break;

    default:
      fromDate = moment(toDate).subtract(1, 'month');
      break;
  }

  if (timeFrame === TimeFrameValueEnum.sinceLastJackpot || timeFrame === TimeFrameValueEnum.all) {
    return;
  }
  // if (
  //   fromDate.month() === toDate.month() &&
  //   fromDate.date() === toDate.date()
  // ) {
  //   return fromDate.format('MMM D, h:mma') + ' - ' + toDate.format('h:mma');
  // }

  return toDate.format('MMM D, h:mma');
};

export function getDeviceOrientation(): 'portrait' | 'landscape' {
  if (window.screen.orientation && window.screen.orientation.type) {
    const orientationType: OrientationType = window.screen.orientation.type;
    if (orientationType === 'landscape-primary' || orientationType === 'landscape-secondary') {
      return 'landscape';
    } else if (orientationType === 'portrait-primary' || orientationType === 'portrait-secondary') {
      return 'portrait';
    }
  }
  if (window.innerWidth > window.innerHeight) {
    return 'landscape';
  } else {
    return 'portrait';
  }
}

export const transformPhoneInput = (inputValue: string) => {
  if (!inputValue) return '';

  const strippedInputValue = inputValue.replace(/-/g, '');
  const hasOnlyNumbers = /^\d+$/.test(strippedInputValue);

  if (!hasOnlyNumbers) return;

  const firstPart = strippedInputValue.slice(0, 3);
  const secondPart = strippedInputValue.slice(3, 6);
  const thirdPart = strippedInputValue.slice(6, 10);
  return `${firstPart}${secondPart ? `-${secondPart}` : ''}${thirdPart ? `-${thirdPart}` : ''}`;
};

export const getOrderAndHeatKey = (
  index: number,
  direction: OrderByEnum,
  insight: InsightValue
) => ({
  order: index + 1,
  heatKey:
    insight === 'jackpot_value' || insight === 'jackpots' || insight === 'pop'
      ? direction === OrderByEnum.Asc
        ? floormapJackpotColdHeatScale
        : floormapTopHeatScale
      : direction === OrderByEnum.Asc
      ? slotHotnessScale.One
      : floormapTopHeatScale,
});

// Reads an EGM's `<insight>_order` rank (1 = highest value for that insight).
// Returns null when the EGM carries no usable rank — the column is not numeric
// server-side, so it can arrive as null, as blank padding, or as a string that
// won't parse. `Number()` alone turns all three into 0, which reads as "rank 0",
// i.e. hotter than the genuine rank 1, and drags unranked EGMs to the top of the
// hottest list.
export const getInsightOrderRank = (
  row: ICasinoReport,
  insightKey: InsightValue
): number | null => {
  const raw = row[`${insightKey}_order`] as string | number | null | undefined;
  if (raw === null || typeof raw === 'undefined') return null;
  const trimmed = typeof raw === 'string' ? raw.trim() : raw;
  if (trimmed === '') return null;
  const rank = Number(trimmed);
  return Number.isFinite(rank) ? rank : null;
};

export const sortByInsightOrder = (
  filteredArray: ICasinoReport[],
  insightKey: InsightValue,
  orderBy = OrderByEnum.Desc
): ICasinoReport[] => {
  return filteredArray.sort((a, b) => {
    const rankA = getInsightOrderRank(a, insightKey);
    const rankB = getInsightOrderRank(b, insightKey);
    // Unranked EGMs sort last in BOTH directions, matching the API's NULLS LAST
    // on the same key — so the list and the map agree on which EGMs lead.
    if (rankA === null || rankB === null) {
      if (rankA === rankB) return 0;
      return rankA === null ? 1 : -1;
    }
    return orderBy === OrderByEnum.Desc ? rankA - rankB : rankB - rankA;
  });
};

export const getTopAndBottom = (filteredArray: ICasinoReport[], insightKey: InsightValue) => {
  // Rank the EGMs that actually have a rank. Unranked ones are dropped rather
  // than sorted to one end: the API keeps them last in BOTH directions, so they
  // never lead either list, and leaving them in would hand `slice(-n)` a block of
  // unranked EGMs and put the coldest pins on machines the list never shows.
  const orderData = sortByInsightOrder(
    filteredArray.filter((row) => getInsightOrderRank(row, insightKey) !== null),
    insightKey
  );

  const top10 = orderData.slice(0, TOP_SLOTS_LENGTH);
  const bottom10 = orderData.slice(-TOP_SLOTS_LENGTH);

  return { top10, bottom10 };
};

export const setHeatKeyForAllItems = (
  slots: ICasinoReport[],
  number: slotHotnessScale
): ICasinoReport[] => {
  slots.forEach((slot) => {
    slot.heatKey = number;
  });
  return slots;
};

export const checkProUser = (subscriptionStatus: string, currentPeriodEnd: number): boolean => {
  const currentTimestamp = Math.floor(new Date().getTime() / 1000);
  return subscriptionStatus === 'active' && currentPeriodEnd > currentTimestamp;
};

export const getCleanGameName = (gameName: string) => {
  if (!gameName) return '';
  const parts = gameName.split('/').map((part: string) => part.trim());
  return parts[0];
};

export const getCleanThemeName = (gameName: string) => {
  if (!gameName) return '';
  const parts = gameName.split(/\/(.*)/).map((part: string) => part.trim());
  return parts[1] && parts[1] !== '' ? parts[1] : '';
};

export const getBankName = (area?: string | number | null, section?: string | number | null): string => {
  const areaPart = area === null || area === undefined ? '' : String(area);
  const sectionPart = section === null || section === undefined ? '' : String(section);
  return `${areaPart}${sectionPart}`;
};

export const toCapitalizedWords = (text: string): string => {
  return text.replace(/\b\w+/g, (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
};

export const isTextFinite = (text: string): boolean => {
  const number = parseInt(text, 10);
  return !isNaN(number) && isFinite(number);
};

export const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getTimeFrameInfo = (timeFrame: string) =>
  config.allTimeFrames.filter((timeframe) => timeframe.value === timeFrame)[0];

export const getResetPasswordErrorMessage = (awsErrorMessage: string) => {
  switch (awsErrorMessage) {
    case INVALID_OTP_CODE_MESSAGE:
    case INVALID_OTP_CODE_MESSAGE2:
      return 'Incorrect OTP code.';
    case SHORT_PASSWORD_MESSAGE:
    case MUST_HAVE_LOWERCASE_CHAR_MESSAGE:
    case MUST_HAVE_UPPERCASE_CHAR_MESSAGE:
    case MUST_HAVE_SYMBOL_CHAR_MESSAGE:
      return 'Please make sure your new password is at least 8 characters long and it has at least one lowercase letter, one uppercase letter, and a symbol character.';
    case ATTEMPS_LIMIT_EXCEEDED_MESSAGE:
      return "You've exceeded attempts limit. Please try again later.";
    default:
      return 'Something went wrong.';
  }
};

export const AWSRumConfig: AwsRumConfig = {
  sessionSampleRate: 1,
  identityPoolId: 'us-east-2:c3ec7475-94ef-441e-9b6a-f059aa3b51e1',
  endpoint: 'https://dataplane.rum.us-east-2.amazonaws.com',
  telemetries: ['performance', 'errors', 'http'],
  allowCookies: true,
  enableXRay: false,
};

export const S3_CASINOS_BASE = 'https://slotcheck-casinos.s3.us-east-1.amazonaws.com';
// Local HTTPS runs hit S3 CORS (allowlist is deploy hosts only). setupProxy.js
// serves the same bucket at /__floorplan so fetchGeoJSON stays same-origin.
export const S3_FLOORPLAN_BASE =
  process.env.REACT_APP_ENV_IS_LOCAL === 'true'
    ? '/__floorplan'
    : 'https://slotcheck-floorplan.s3.us-east-2.amazonaws.com';
export const S3_PROFILE_BASE = `${S3_CASINOS_BASE}/assets/profile`;


export const getCasinoFolderName = (casinoId?: number): string | undefined => {
  switch (casinoId) {
    case 1:
      return 'lotc';
    case 2:
      return 'eureka';
    case 3:
      return 'redwind';
    case 4:
      return 'slotcheck';
    case 22:
      return 'silversevens';
    case 23:
      return 'ach';
    default:
      return undefined;
  }
};
